import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { cmApi } from '@src/api';
import { dataControlStore } from '@stores/dataControlStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import type { TableComponentRequestDto } from '@src/api';

import styles from './PackageLevelFilter.module.scss';

export const PackageLevelFilter = observer(() => {
  const [controlNames, setControlNames] = useState<{ ctrl_vers_code: string }[]>([]);
  const [statusItems, setStatusItems] = useState<{ id: string; name: string }[]>([]);

  const {
    filters,
    setFilters,
    lastPagesParam: { reportPackageCodeCm },
  } = dataControlStore;
  const { controlVersions } = controlVersionsStore;

  const { controlName, status } = filters.packageLevel || {};
  const { organization } = filters.generalReport || {};

  useEffect(() => {
    fetchReferences(['controlVersions']);
  }, []);

  useEffect(() => {
    const tableInfo = {
      aggregate: {},
      fields: ['ctrl_vers_code'],
      filter: [],
      groupBy: [],
      hardFilter: [
        [
          {
            name: 'org_code',
            type: FILTER.EQ,
            filterIsInverted: false,
            values: [organization],
          },
        ],
      ],
      limit: 1000,
      offset: 0,
      openGroup: [],
      orderBy: [],
      resource: 'ctrl.s_main',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as { data: { ctrl_vers_code: string }[] };
      if (result && result.data && result.data.length > 0) {
        setControlNames(result.data);
      }
    });
  }, [organization]);

  useEffect(() => {
    const tableInfo = {
      fields: ['status', 'status_text'],
      hardFilter: [
        [
          {
            name: 'status_schema',
            type: FILTER.EQ,
            filterIsInverted: false,
            values: ['CTRL_2'],
          },
        ],
      ],
      limit: 100,
      offset: 0,
      resource: 'ctrl.nsi_ctrl_status',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as { data: { status: string; status_text: string }[] };
      if (result && result.data && result.data.length > 0) {
        const out = result.data.map((item) => ({
          id: item.status,
          name: `${item.status} - ${item.status_text}`,
        }));
        setStatusItems(out);
      }
    });
  }, []);

  const controlNameItems = useMemo(() => {
    const out: { id: string; name: string }[] = [];

    const group = reportPackageCodeCm?.substring(
      reportPackageCodeCm?.lastIndexOf('_') + 1,
      reportPackageCodeCm?.lastIndexOf('Y'),
    );

    controlNames.forEach((item) => {
      const controlVersion = controlVersions?.find((el) => el.ctrlVersCode === item.ctrl_vers_code);

      if (controlVersion?.repGroupCode === group) {
        out.push({
          id: controlVersion?.ctrlVersExt ?? '',
          name: controlVersion?.ctrlVersExt ?? '',
        });
      }
    });

    return out;
  }, [controlNames, controlVersions, reportPackageCodeCm]);

  const onChangeControlName = useCallback(
    (value) => {
      setFilters({ ...filters.packageLevel, controlName: value }, 'packageLevel');
    },
    [filters.packageLevel, setFilters],
  );

  const onChangeStatus = useCallback(
    (value) => {
      setFilters({ ...filters.packageLevel, status: value }, 'packageLevel');
    },
    [filters.packageLevel, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    setFilters({}, 'packageLevel');
  }, [setFilters]);

  const disabled = useMemo(() => !Object.keys(filters.packageLevel ?? {}).length, [filters.packageLevel]);

  return (
    <div className={styles.designFilters}>
      <div className={styles.filterItems}>
        <Select
          items={controlNameItems}
          placeholder="Код контроля"
          value={controlName}
          size="small"
          onChange={onChangeControlName}
          showSearch
          className={styles.select}
        />

        <Select
          items={statusItems}
          placeholder="Статус"
          value={status}
          size="small"
          onChange={onChangeStatus}
          showSearch
          className={styles.selectStatus}
        />
      </div>

      <Button onClick={handleResetFilters} disabled={disabled} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
